import { useAuth } from '@/composables/useAuth'
import { useErrorReporter } from '@/composables/useErrorReporter'
import { useNotes } from '@/composables/useNotes'
import { usePermissions } from '@/composables/usePermissions'
import { useSystemEmailSend } from '@/composables/useSystemEmailSend'
import { useToast } from '@/composables/useToast'
import {
	API_BASE,
	INFO_EMAIL,
	PORTAL_BASE,
	SUPPORT_INTERNAL_EMAIL
} from '@/constants'
import { TICKET_STATUS_OPTIONS, TICKET_TIER_OPTIONS } from '@/constants/ticketing'
import type {
	DynamicObject,
	MappedTicket,
	TicketComment,
	TicketCommentCreate,
	TicketCreatePayload,
	TicketsApiResponse,
	TicketUpdateObject
} from '@/types/ticketing'
import { mapTickets } from '@/utils/mappers/TicketMap'
import {
	computed,
	onMounted,
	ref
} from 'vue'

const { contactId } = useAuth()

const allTickets = ref<MappedTicket[]>([])
const accountTickets = ref<MappedTicket[]>([])
const activeAccountId = ref('')

const activeUserOpenTicketCount = computed(() => {
	return allTickets.value?.filter((ticket: MappedTicket) => ticket.status === TICKET_STATUS_OPTIONS.OPEN && ticket.assigneeId === contactId.value).length || 0
})

const hasOpenTickets = computed(() => accountTickets.value?.some((ticket: MappedTicket) => ticket.status === TICKET_STATUS_OPTIONS.OPEN))
const accountOpenTicketCount = computed(() => accountTickets.value?.filter((ticket: MappedTicket) => ticket.status === TICKET_STATUS_OPTIONS.OPEN).length || 0)
const formerAccountId = ref('')

const accountTicketsLoading = ref(false)
const allTicketsLoading = ref(false)
const updateLoading = ref(false)
const commentsLoading = ref(false)

const comments = ref<TicketComment[]>([])
const formerTicketId = ref('')

export function useTicketing() {
	const error = useError()
	const { bugsnagReport } = useErrorReporter()
	const { addToast } = useToast()
	const { setNote } = useNotes()
	const {
		fetchPermissions,
		permissableUsers,
		accountServiceRepOptions
	} = usePermissions()

	onMounted(() => {
		if (!permissableUsers.value?.length) {
			fetchPermissions('notes.create')
		}
	})

	const createTicket = async (ticketData: TicketCreatePayload) => {
		const createdTime = new Date()
		const assigneeString = ticketData.assignee?.firstName || 'Unassigned'
		const payloadToSend = {
			...ticketData,
			category: ticketData.category.name,
			priority: ticketData.priority.name,
			tier: ticketData.tier.name,
			createdTime
		}

		const { sendSystemEmail } = useSystemEmailSend()
		await $fetch(`${API_BASE}/mongo/tickets`,	{
			method: 'POST',
			credentials: 'include',
			body: { doc: JSON.stringify(payloadToSend) },
			async onResponse({ response }) {
				if (!response?.ok) {
					throw new Error(response._data?.error || response._data?.errors?.[0])
				}
				addToast({
					notificationType: 'success',
					title: 'Ticket created'
				})
				await fetchTicketsOnAccount(ticketData.accountId, true)

				const noteConfig = {
					note: `TICKET CREATED \rTitle: <a target="_blank" href="${PORTAL_BASE}/accounts/${ticketData.accountId}/ticketing/${response._data?.id?.$oid}" class="text-mx-orange hover:underline">${ticketData.title}</a> \rId: ${response._data.id?.$oid}`,
					accountId: ticketData.accountId,
					category: 'ticket'
				}
				await setNote(noteConfig)

				const emailConfig = {
					to: ticketData.assignee?.email || SUPPORT_INTERNAL_EMAIL, // REVIEW: should this be the assignee's email or the main support email?
					from: INFO_EMAIL,
					subject: `New Ticket Created: ${ticketData.title}`,
					body: `A new ticket has been created. \r\n Title: <a target="_blank" href="${PORTAL_BASE}/accounts/${ticketData.accountId}/ticketing/${response._data?.id?.$oid}">${ticketData.title}</a> \r\n Description: ${ticketData.content} \r\n Account: ${ticketData.accountId} \r\n Assignee: ${assigneeString}`
				}

				await sendSystemEmail(emailConfig)
			}
		}).catch(e => {
			bugsnagReport({
				error: e instanceof Error ? e : new Error('createTicket - catch: ' + (e as Error).toString()),
				showToast: true
			})
		})
	}

	const fetchTickets = async () => {
		if (allTicketsLoading.value) {
			return
		} // Prevents multiple fetches

		allTicketsLoading.value = true

		await fetchPermissions('notes.create')

		await $fetch(`${API_BASE}/mongo/get/tickets`, {
			method: 'POST',
			credentials: 'include',
			onResponse({ response }) {
				if (!response?.ok) {
					throw new Error(response._data?.error || response._data?.errors?.[0])
				}
				const data = response._data.docs as TicketsApiResponse[]
				allTickets.value = mapTickets(data).sort((a, b) => {
					return new Date(b.lastUpdated || '')?.getTime() - new Date(a.lastUpdated || '')?.getTime()
				})
				allTicketsLoading.value = false
			}
		}).catch(e => {
			bugsnagReport({
				error: e instanceof Error ? e : new Error('fetchTickets - catch: ' + (e as Error).toString()),
				showToast: true
			})
			allTicketsLoading.value = false
		})
	}

	const fetchTicketsOnAccount = async (accountId: string, refetch?: boolean) => {
		if (!accountId) {
			return
		}
		if (accountId && formerAccountId.value === accountId && !refetch) {
			return
		}

		await fetchPermissions('notes.create')

		formerAccountId.value = accountId || ''
		activeAccountId.value = accountId || ''

		const data = { mongoQueryFilters: { filters: { accountId } } }

		accountTicketsLoading.value = true
		await $fetch(`${API_BASE}/mongo/get/tickets`, {
			method: 'POST',
			credentials: 'include',
			body: JSON.stringify(data),
			onResponse({ response }) {
				if (!response?.ok) {
					throw new Error(response._data?.error || response._data?.errors?.[0])
				}
				if (response.status === 204) {
					accountTickets.value = []
				} else {
					const data = response._data.docs as TicketsApiResponse[]
					accountTickets.value = mapTickets(data).sort((a, b) => {
						return new Date(b.lastUpdated || '')?.getTime() - new Date(a.lastUpdated || '')?.getTime()
					})
				}
				accountTicketsLoading.value = false
			}
		}).catch(e => {
			bugsnagReport({
				error: e instanceof Error ? e : new Error('fetchTicketsById - catch: ' + (e as Error).toString()),
				showToast: true
			})
			accountTicketsLoading.value = false
		})
	}

	const fetchComments = (ticketId?: string, refetch?: boolean) => {
		if (formerTicketId.value === ticketId && !refetch) {
			return
		}
		formerTicketId.value = ticketId || ''
		commentsLoading.value = true
		$fetch(`${API_BASE}/mongo/get/ticketsComments`, {
			method: 'POST',
			credentials: 'include',
			onResponse({ response }) {
				if (!response?.ok) {
					commentsLoading.value = false
					throw new Error(response._data?.error || response._data?.errors?.[0])
				}
				comments.value = ticketId ? response._data.docs.filter((comment: any) => comment?.ticketId === ticketId)?.reverse() : response._data.docs?.reverse()
				commentsLoading.value = false
			}
		}).catch(e => {
			bugsnagReport({
				error: e instanceof Error ? e : new Error('fetchComments - catch: ' + (e as Error).toString()),
				showToast: true
			})
			commentsLoading.value = false
		})
	}

	const createComment = (commentData: TicketCommentCreate) => {
		const { sendSystemEmail } = useSystemEmailSend()
		commentsLoading.value = true
		$fetch(`${API_BASE}/mongo/ticketsComments`, {
			method: 'POST',
			credentials: 'include',
			body: { doc: JSON.stringify(commentData) },
			onResponse({ response }) {
				if (!response?.ok) {
					commentsLoading.value = false
					throw new Error(response._data?.error || response._data?.errors?.[0])
				}

				const author = `${commentData.author.firstName} ${commentData.author.lastName}`.trim()
				const accountId = commentData.accountId || activeAccountId.value || ''
				const ticketId = commentData.ticketId || ''
				const ticketTitle = commentData.ticketTitle || ''
				const comment = commentData.content || ''
				const note = `New comment ${'by ' + author} \r\n\n <span class="font-semibold">Ticket:</span> <a target="_blank" href="${PORTAL_BASE}/accounts/${accountId}/ticketing/${ticketId}" class="text-mx-orange hover:underline">${ticketTitle}</a> \r<span class="font-semibold">Comment:</span> ${comment}`
				const noteConfig = {
					note,
					accountId,
					category: 'ticket'
				}
				if (accountId && ticketId) {
					setNote(noteConfig)
					addToast({
						notificationType: 'success',
						title: 'Comment created'
					})
					sendSystemEmail({
						to: SUPPORT_INTERNAL_EMAIL,
						from: 'info@maverixbroadband.com',
						subject: `Ticket Comment Created: ${ticketTitle}`,
						body: `The status of ticket <a target="_blank" href="${PORTAL_BASE}/accounts/${accountId}/ticketing/${ticketId}">${ticketTitle}</a> has a new comment from ${author}: \r \n ${comment}`
					})
				}

				fetchComments(commentData.ticketId, true)
				commentsLoading.value = false
			}
		}).catch(e => {
			bugsnagReport({
				error: e instanceof Error ? e : new Error('createComment - catch: ' + (e as Error).toString()),
				showToast: true
			})
			commentsLoading.value = false
		})
	}

	const updateTicket = async (data: { ticketData: TicketUpdateObject
		accountId: string
		email?: string | null
		title: string }) => {
		const {
			ticketData, accountId, email, title
		} = data || {}
		const { sendSystemEmail } = useSystemEmailSend()
		updateLoading.value = true
		const getUpdateText = (update: DynamicObject) => {
			const updateObj = update
			const updateText = Object.keys(updateObj).map(key => {
				return key === 'assigneeId' ? `ASSIGNEE: ${accountServiceRepOptions.value?.find(user => user.id === updateObj[key])?.name}` : `${key.toUpperCase()}: ${updateObj[key]}`
			}).join(', ')
			return updateText
		}
		const updateText = getUpdateText(ticketData.update)

		ticketData.update.updated = new Date()
		if (ticketData.update.tier === TICKET_TIER_OPTIONS.TIER2) {
			ticketData.update.escalated = new Date()
		}

		const payload = {
			key: ticketData.key,
			value: ticketData.value,
			update: JSON.stringify(ticketData.update)
		}

		await $fetch(`${API_BASE}/mongo/tickets`, {
			method: 'PUT',
			credentials: 'include',
			body: payload,
			onResponse({ response }) {
				if (!response?.ok) {
					updateLoading.value = false
					throw new Error(response._data?.error || response._data?.errors?.[0])
				}
				addToast({
					notificationType: 'success',
					title: 'Ticket updated'
				})
				setNote({
					note: `TICKET UPDATED \rTitle: <a target="_blank" href="${PORTAL_BASE}/accounts/${accountId}/ticketing/${ticketData.value}" class="text-mx-orange hover:underline">${title}<a> \r Update: ${updateText}`,
					accountId,
					category: 'ticket'
				})
				sendSystemEmail({
					to: email ?? SUPPORT_INTERNAL_EMAIL,
					from: 'info@maverixbroadband.com',
					subject: `Ticket Status Updated: ${title}`,
					body: `The status of ticket <a target="_blank" href="${PORTAL_BASE}/accounts/${accountId}/ticketing/${ticketData.value}">${title}</a> has been updated. \r\n Update: \r\n ${updateText}`
				})
			}
		}).catch(e => {
			bugsnagReport({
				error: e instanceof Error ? e : new Error('updateTicket - catch: ' + (e as Error).toString()),
				showToast: true
			})
		}).finally(async () => {
			await fetchTicketsOnAccount(accountId, true)
			updateLoading.value = false
		})
	}

	const getTicketById = async (ticketId: string, accountId?: string) => {
		if (accountId) {
			await fetchTicketsOnAccount(accountId)
		}
		const accountsTicketFound = accountTickets.value?.find((ticket: MappedTicket) => ticket?.id?.toString() === ticketId?.toString())
		const allTicketsFound = allTickets.value?.find((ticket: MappedTicket) => ticket?.id?.toString() === ticketId?.toString())
		if (accountId && !accountsTicketFound) {
			// error.value from Nuxt's useError composable
			error.value = createError({
				statusCode: 204,
				statusMessage: 'Ticket not found.'
			})
		}
		return accountsTicketFound || allTicketsFound
	}

	return {
		createTicket,
		fetchTickets,
		fetchTicketsOnAccount,
		accountTickets,
		allTickets,
		hasOpenTickets,
		getTicketById,
		fetchComments,
		comments,
		createComment,
		updateTicket,
		allTicketsLoading,
		accountTicketsLoading,
		updateLoading,
		commentsLoading,
		accountServiceRepOptions,
		activeUserOpenTicketCount,
		accountOpenTicketCount
	}
}
